// This file is automatically compiled by Vite, along with any other files
// present in this directory. You're encouraged to place your actual application logic in
// a relevant structure within app/javascript and only use these pack files to reference
// that code so it'll be compiled.

import '~/styles/application.css';

import { createApp, h } from 'vue';
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import Tooltip from 'primevue/tooltip';
import * as Sentry from '@sentry/vue';

import { router, createInertiaApp } from '@inertiajs/vue3';

// Font Awesome
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

// Add all icons to the library
library.add(fas, far, fab);
import Plausible from 'plausible-tracker';
const plausibleUrl = document.querySelector(
  'meta[name="plausible-url"]',
).content;
if (plausibleUrl) {
  const plausible = Plausible({
    domain: document.querySelector('meta[name="app-host"]').content,
    apiHost: plausibleUrl,
  });

  router.on('navigate', () => {
    plausible.trackPageview();
  });
}

import * as Routes from '@/routes.js';
import ToastService from 'primevue/toastservice';

const pages = import.meta.glob('../Pages/**/*.vue', { eager: true });

const getNonce = () => {
  const meta = document.querySelector('meta[name="csp-nonce"]');
  if (meta) return meta.getAttribute('content');
  console.warn('CSP nonce not found in meta tag.');
  return null;
};

createInertiaApp({
  resolve: (name) => {
    const component = pages[`../Pages/${name}.vue`];
    if (!component)
      throw new Error(
        `Unknown page ${name}. Is it located under Pages with a .vue extension?`,
      );

    return component;
  },

  title: (title) =>
    title ? `${title} | Arcadia Consulting` : 'Arcadia Consulting',

  setup({ el, App, props, plugin }) {
    const vueApp = createApp({
      render: () => h(App, props),
    });

    Sentry.init({
      vueApp,
      dsn: import.meta.env.VITE_SENTRY_FRONTEND,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      integrations: [Sentry.replayIntegration()],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
      // Logs
      enableLogs: true,
    });

    vueApp.config.globalProperties.$routes = Routes;
    vueApp.use(plugin);
    vueApp.use(PrimeVue, {
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primevue',
            order: 'theme, base, primevue',
          },
        },
      },
      csp: {
        nonce: getNonce(),
      },
    });
    vueApp.use(ToastService);
    vueApp.directive('tooltip', Tooltip);
    vueApp.component('FontAwesomeIcon', FontAwesomeIcon);
    vueApp.mount(el);
  },
});
